import { useMemo } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { IntelligencePeriodPreset } from "@/api/intelligence";

export type IntelligencePeriodFilterValue = {
  preset: IntelligencePeriodPreset;
  from?: string;
  to?: string;
};

type HospitalOption = { id: string; name: string };

type Props = {
  hospitals: HospitalOption[];
  showHospitalFilter: boolean;
  hospitalId: string;
  onHospitalChange: (id: string) => void;
  period: IntelligencePeriodFilterValue;
  onPeriodChange: (next: IntelligencePeriodFilterValue) => void;
};

const PERIOD_OPTIONS: { value: IntelligencePeriodPreset; label: string }[] = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "this_week", label: "This Week" },
  { value: "previous_week", label: "Previous Week" },
  { value: "this_month", label: "This Month" },
  { value: "previous_month", label: "Previous Month" },
  { value: "this_quarter", label: "This Quarter" },
  { value: "previous_quarter", label: "Previous Quarter" },
  { value: "this_year", label: "This Year" },
  { value: "previous_year", label: "Previous Year" },
  { value: "custom", label: "Custom Range" },
];

export function CommandCentreFilters({
  hospitals,
  showHospitalFilter,
  hospitalId,
  onHospitalChange,
  period,
  onPeriodChange,
}: Props) {
  const periodLabel = useMemo(
    () => PERIOD_OPTIONS.find((p) => p.value === period.preset)?.label ?? "Period",
    [period.preset]
  );

  return (
    <div className="flex flex-wrap items-end gap-3 rounded-xl border bg-card p-4 shadow-sm">
      {showHospitalFilter && (
        <div className="flex flex-col gap-1.5 min-w-[200px]">
          <Label className="text-xs text-muted-foreground">Hospital</Label>
          <Select value={hospitalId} onValueChange={onHospitalChange}>
            <SelectTrigger>
              <SelectValue placeholder="Hospital" />
            </SelectTrigger>
            <SelectContent>
              {hospitals.length > 1 && <SelectItem value="all">All hospitals (consolidated)</SelectItem>}
              {hospitals.map((h) => (
                <SelectItem key={h.id} value={h.id}>
                  {h.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="flex flex-col gap-1.5 min-w-[200px]">
        <Label className="text-xs text-muted-foreground">Period</Label>
        <Select
          value={period.preset}
          onValueChange={(v) =>
            onPeriodChange({
              preset: v as IntelligencePeriodPreset,
              from: v === "custom" ? period.from : undefined,
              to: v === "custom" ? period.to : undefined,
            })
          }
        >
          <SelectTrigger>
            <SelectValue>{periodLabel}</SelectValue>
          </SelectTrigger>
          <SelectContent>
            {PERIOD_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {period.preset === "custom" && (
        <>
          <div className="flex flex-col gap-1.5">
            <Label className="text-xs text-muted-foreground">From</Label>
            <Input
              type="date"
              className="w-[160px]"
              value={period.from ?? ""}
              onChange={(e) => onPeriodChange({ ...period, from: e.target.value })}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label className="text-xs text-muted-foreground">To</Label>
            <Input
              type="date"
              className="w-[160px]"
              value={period.to ?? ""}
              onChange={(e) => onPeriodChange({ ...period, to: e.target.value })}
            />
          </div>
        </>
      )}
    </div>
  );
}
